import { Button, Text, Tooltip, makeStyles, shorthands, tokens } from '@fluentui/react-components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionFilled } from '@fluentui/react-icons';
import { appInsights } from '../../AppInsights';
import { UserInteractionEventProperties, EventName_UserInteraction, EventProperty_Value_ApplicationType, EventProperty_Value_ReleaseNotesClick } from '../../store/models/customEvents';
import { useUserContext } from '../../Contexts/UserContext';
import './Footer.scss';

const AlertTriangleIcon: FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
        <path d="M13.6653 10.5093L8.332 1.17593C8.21571 0.970734 8.04707 0.800056 7.84328 0.68131C7.63949 0.562564 7.40786 0.5 7.172 0.5C6.93614 0.5 6.7045 0.562564 6.50071 0.68131C6.29693 0.800056 6.12829 0.970734 6.012 1.17593L0.678664 10.5093C0.561119 10.7128 0.499483 10.9439 0.500003 11.1789C0.500524 11.414 0.563183 11.6448 0.681628 11.8478C0.800074 12.0509 0.970096 12.219 1.17446 12.3352C1.37883 12.4513 1.61027 12.5114 1.84533 12.5093H12.512C12.7459 12.509 12.9757 12.4472 13.1782 12.3301C13.3807 12.213 13.5488 12.0447 13.6657 11.842C13.7825 11.6394 13.844 11.4095 13.844 11.1756C13.8439 10.9417 13.7823 10.7119 13.6653 10.5093Z" stroke="#F97316" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.172 4.5V8" stroke="#F97316" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.172 10H7.17867" stroke="#F97316" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const useStyles = makeStyles({
    footerContainer: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        ...shorthands.padding('0', '0', '8px', '0'),
        '@media (max-width: 768px)': {
            // Center the footer and align with the prompt box
            justifyContent: 'center',
            paddingLeft: '0',
            paddingRight: '0',
        },
    },
    footerCentral: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        ...shorthands.gap('4px'),
        borderRadius: '6px',
        backgroundColor: 'var(--Colors-Orange-100, #FFEDD5)',
        ...shorthands.padding('6px', '8px'),
        maxWidth: '960px',
        width: '960px',
        boxSizing: 'border-box',
        // Desktop margins - removed to align with input-container
        ...shorthands.margin('0', 'auto'),
        '@media (max-width: 768px)': {
              // Match the prompt box width on mobile
              // Allow the icon and text to wrap and center when text spans multiple lines
              flexWrap: 'wrap',
              alignItems: 'center',
            ...shorthands.margin('0', 'auto'),
            ...shorthands.padding('6px', '8px'),
            width: '95%',
            maxWidth: '95%',
            boxSizing: 'border-box',
            ...shorthands.gap('6px'),
        },
    },
    footerLateral: {
        minWidth: '100px',
    },
    footerLateralLeft: {
        minWidth: '100px',
        '@media (max-width: 768px)': {
            // Hide left lateral space on mobile
            minWidth: '0px',
            width: '0px',
            flex: '0 0 auto',
        },
    },
    footerLateralRight: {
        minWidth: '100px',
        '@media (max-width: 768px)': {
            // Ultra-minimal width to fit version button on mobile
            minWidth: '0px',
            width: 'auto',
            flex: '0 0 auto',
            paddingLeft: '2px', // Minimal padding for separation
            '& button': {
                minWidth: '0px',
                padding: '4px 6px', // Compact button padding
                fontSize: '0.8rem', // Slightly smaller font
            },
        },
    },
    helpButton: {
        backgroundColor: tokens.colorNeutralForeground1,
    },
    // Show desktop message by default, hide on mobile
    desktopMessage: {
        display: 'block',
        // Exact typography requested
        textAlign: 'center',
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        letterSpacing: '0.06px',
        color: 'var(--Colors-Neutral-900)',
        whiteSpace: 'normal',
        '@media (max-width: 768px)': {
            display: 'none',
        },
    },
    // Hide mobile message by default, show only on mobile
    mobileMessage: {
        display: 'none',
        // Apply same typography as desktop but optimized for wrapping
        textAlign: 'center',
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        letterSpacing: '0.06px',
        color: 'var(--Colors-Neutral-900)',
        '@media (max-width: 768px)': {
            display: 'block',
            // Allow text to wrap and center on multiple lines
            whiteSpace: 'normal',
            textAlign: 'center',
            margin: '0',
            padding: '0 2px',
            flex: '1',
            boxSizing: 'border-box',
        },
    },
});

export const Footer: FC = () => {
    const { t } = useTranslation();
    const classes = useStyles();
    return (
    <div className={`${classes.footerContainer} footer-container-margin`}>
            <div className={classes.footerLateralLeft} />
            <div className={`${classes.footerCentral} footer-central-dark-mode`}>
                <AlertTriangleIcon />
                {/* Desktop message - full disclaimer */}
                <Text className={classes.desktopMessage}>{t('contentDisclaimer')}</Text>
                <Text className={classes.mobileMessage}>{t('contentDisclaimer')}</Text>
            </div>
            {/* Version removed from footer, now in header */}
            <div className={classes.footerLateralRight}></div>
        </div>
    );
};
